import { Link } from 'react-router-dom';
import type { DocsConfig, DocsOrg } from '@/api/docs';
import { Chip } from '@/components/network/NetworkKit';
import { companyPath, rmmDevicePath } from '@/lib/routes';

/** Small shared pieces for the Docs (IT Glue) pages and cards. */

/** The org's Autotask company — linked by IT Glue's PSA integration, so "fix" means fixing it in IT Glue. */
export function OrgCompany({ org }: { org: Pick<DocsOrg, 'autotaskCompanyId' | 'name' | 'psa'> & { companyName?: string | null } }) {
  if (org.autotaskCompanyId != null) {
    return <Link to={companyPath(org.autotaskCompanyId)} className="block truncate text-foreground hover:underline">{org.companyName ?? org.name}</Link>;
  }
  return <span className="italic text-muted-foreground" title="Link the organisation to its Autotask company in IT Glue (PSA integration)">{org.psa === 'orphaned' ? 'orphaned link' : 'not linked'}</span>;
}

/** IT Glue's expiration kinds are type names ("SslCertificate"); show them as words. */
export const kindLabel = (kind: string) => (kind === 'SslCertificate' ? 'SSL certificate' : kind);

/** Days from today to an ISO date (negative = past). */
export function daysUntil(date: string | null): number | null {
  if (!date) return null;
  const t = new Date(date).getTime();
  return Number.isFinite(t) ? Math.round((t - Date.now()) / 86_400_000) : null;
}

export function ExpiryChip({ date }: { date: string | null }) {
  const d = daysUntil(date);
  if (d == null) return <span className="text-xs text-muted-foreground">—</span>;
  const label = date!.slice(0, 10);
  if (d < 0) return <Chip tone="bad" title={label}>Expired {-d}d ago</Chip>;
  if (d <= 30) return <Chip tone="bad" title={label}>{d}d left</Chip>;
  if (d <= 90) return <Chip tone="warn" title={label}>{d}d left</Chip>;
  return <span className="text-xs tabular-nums text-muted-foreground">{label}</span>;
}

/** The configuration's Datto RMM device: linked, gone from Datto, or not in RMM at all. */
export function RmmCell({ c }: { c: DocsConfig }) {
  if (c.rmm) {
    return (
      <Link to={rmmDevicePath(c.rmm.uid)} className="inline-flex max-w-[200px] items-center gap-1.5 text-xs text-foreground hover:underline" title={c.rmm.hostname} onClick={(e) => e.stopPropagation()}>
        <span className={c.rmm.online ? 'h-1.5 w-1.5 shrink-0 rounded-full bg-success' : 'h-1.5 w-1.5 shrink-0 rounded-full bg-muted-foreground'} title={c.rmm.online ? 'Online' : 'Offline'} />
        <span className="truncate">{c.rmm.hostname}</span>
      </Link>
    );
  }
  if (c.dattoDeviceId != null) return <Chip tone="warn" title={`IT Glue links Datto device #${c.dattoDeviceId}, which Datto RMM no longer has`}>Device gone</Chip>;
  return <span className="text-xs text-muted-foreground">—</span>;
}

/** Device-like configuration types — the ones expected to have a Datto RMM agent. */
export const AGENT_TYPES = /workstation|laptop|desktop|server|computer|notebook/i;
